// ChartWidgets.jsx
// Complete implementation of all 24 chart types from default templates
// ✅ FIXED: Proper data extraction matching backend scan file structure

import React, { useMemo } from 'react';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, PointElement, LineElement, RadialLinearScale, Title } from 'chart.js';
import { Bar, Pie, Doughnut, Line, Radar, PolarArea, Bubble } from 'react-chartjs-2';
import { getLabel, getLabelStyle } from './widgetLabelHelpers';
import './ChartWidgets.css';

// Register Chart.js components
ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  RadialLinearScale,
  Title
);

// Default color palette (matches backend templates)
const DEFAULT_COLORS = [
  '#6fca42', // Green (rgb(111, 202, 66))
  '#fdbc13', // Yellow (rgb(253, 188, 19))
  '#d71920', // Red (rgb(215, 25, 32))
  '#093070', // Blue (rgb(9, 48, 112))
  '#9ca3af', // Gray
  '#3b82f6', // Light Blue
  '#f59e0b', // Orange
  '#8b5cf6', // Purple
  '#10b981', // Emerald
  '#f43f5e'  // Rose
];

// ============================================================================
// HELPERS
// ============================================================================

// Helper to map CSS to Chart.js font options
const getChartFont = (style) => ({
  size: style?.fontSize ? parseInt(style.fontSize) : 12,
  weight: style?.fontWeight || 'normal',
  family: style?.fontFamily || "'Inter', sans-serif"
});

// Helper to map CSS textAlign to Chart.js alignment
const textAlignToChartAlign = (textAlign) => {
  if (textAlign === 'left') return 'start';
  if (textAlign === 'right') return 'end';
  return 'center';
};

// Helper to get consistent title options
const getTitleOptions = (config, defaultTitle, widgetType) => {
  // Use specific widget type from config if available, otherwise fallback to generic type (bar, pie, etc)
  const typeToUse = config?.widgetType || widgetType;
  
  const style = getLabelStyle(config, 'title', {
    fontSize: '16px',
    fontWeight: 'bold',
    color: '#000000',
    textAlign: 'center'
  }, typeToUse);

  return {
    display: true,
    text: config?.title || defaultTitle, // Prioritize config title if set, else default
    font: getChartFont(style),
    color: style?.color || '#000000',
    align: textAlignToChartAlign(style?.textAlign || 'center'),
    padding: { top: 10, bottom: 20 }
  };
};

// Helper to check if chart data is empty
const isChartEmpty = (chartData) => {
  if (!chartData || !chartData.datasets || chartData.datasets.length === 0) return true;
  return chartData.datasets.every(ds =>
    !ds.data || ds.data.length === 0 || ds.data.every(v => v === 0)
  );
};

// ============================================================================
// ACTIVE DIRECTORY CHARTS (8 charts)
// ============================================================================

/**
 * Chart 1: AD Object Types
 * Bar chart showing Users, Computers, Groups, OUs counts
 * Data path: data['Ad Scan'] (matches backend template2/AD.js)
 */
export const ADObjectTypesChart = ({ data, config = {}, style = {} }) => {


  const chartData = useMemo(() => {
    // Extract AD data - scan files use 'Ad Scan' key
    const adData = data?.['Ad Scan'] || data?.Ad || data?.ad || {};
    const users = adData?.['AD Users']?.[0]?.length || adData?.users?.length || 0;
    const computers = adData?.Computers?.length || adData?.computers?.length || 0;
    const groups = adData?.groups?.length || 0;
    const ous = adData?.OU_list?.length || adData?.ou_list?.length || 0;

    return {
      labels: ['Users', 'Computers', 'Groups', 'OUs'],
      datasets: [{
        label: 'Count',
        data: [users, computers, groups, ous],
        backgroundColor: DEFAULT_COLORS.slice(0, 4)
      }]
    };
  }, [data]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { 
        display: !!config.legendPosition && config.legendPosition !== 'none',
        position: config.legendPosition || 'top' 
      },
      title: getTitleOptions(config, 'Active Directory Object Counts', 'bar')
    }
  };

  if (isChartEmpty(chartData)) return null;

  return (
    <div className="chart-widget" style={style}>
      <Bar data={chartData} options={options} />
    </div>
  );
};

/**
 * Chart 2: AD Objects Pie Chart
 * Pie chart showing Users, Computers, Groups, OUs distribution
 * Matches Ad_Objects_status chart from template2/AD.js:355-362
 */
export const ADObjectsPieChart = ({ data, config = {}, style = {} }) => {


  const chartData = useMemo(() => {
    // Extract AD data - matches _Ad_Objects_status logic
    const adData = data?.['Ad Scan'] || data?.Ad || data?.ad || {};
    const userCount = adData?.['AD Users']?.[0]?.length || 0;
    const computerCount = adData?.Computers?.length || 0;
    const groupCount = adData?.groups?.length || 0;
    const ouCount = adData?.OU_list?.length || 0;

    // Match backend: if all values are 0, return null (but we'll show empty chart)
    const values = [userCount, computerCount, groupCount, ouCount];

    return {
      labels: ['Users', 'Computers', 'Groups', 'OUs'],
      datasets: [{
        data: values,
        // Match backend colors: ["blue", "green", "yellow", 'red']
        backgroundColor: ['#3b82f6', '#10b981', '#fdbc13', '#d71920'] // blue, green, yellow, red
      }]
    };
  }, [data]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: config.legendPosition !== 'none',
        position: config.legendPosition || 'top',
        labels: {
          boxWidth: 12,
          padding: 10,
          font: { size: 11 },
          generateLabels: function (chart) {
            const data = chart.data;
            if (data.labels.length && data.datasets.length) {
              const dataset = data.datasets[0];
              return data.labels.map((label, i) => {
                const value = dataset.data[i] || 0;
                return {
                  text: `${label}: ${value}`,
                  fillStyle: dataset.backgroundColor[i],
                  hidden: false,
                  index: i
                };
              });
            }
            return [];
          }
        }
      },
      title: getTitleOptions(config, 'AD Objects Distribution', 'pie'),
      tooltip: {
        callbacks: {
          label: function (context) {
            const label = context.label || '';
            const value = context.parsed || 0;
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = total > 0 ? Math.round((value / total) * 100) : 0;
            return `${label}: ${value} (${percentage}%)`;
          }
        }
      }
    }
  };

  if (isChartEmpty(chartData)) return null;

  return (
    <div className="chart-widget" style={style}>
      <Pie data={chartData} options={options} />
    </div>
  );
};

/**
 * Chart 2: FSMO Roles
 * Bar chart showing FSMO roles status
 */
export const FSMORolesChart = ({ data, config = {}, style = {} }) => {


  const chartData = useMemo(() => {
    const adData = data?.['Ad Scan'] || data?.Ad || data?.ad || {};
    const fsmo = adData?.fsmo_roles || adData?.['FSMO Roles'] || {};

    const labels = ['Schema Master', 'Domain Naming', 'RID Master', 'Infrastructure', 'PDC Emulator'];
    const values = [
      fsmo['Schema Master'] ? 1 : 0,
      fsmo['Domain Naming Master'] ? 1 : 0,
      fsmo['RID Master'] ? 1 : 0,
      fsmo['Infrastructure Master'] ? 1 : 0,
      fsmo['PDC Emulator'] ? 1 : 0
    ];

    return {
      labels,
      datasets: [{
        label: 'Status',
        data: values,
        backgroundColor: DEFAULT_COLORS
      }]
    };
  }, [data]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { 
        display: !!config.legendPosition && config.legendPosition !== 'none',
        position: config.legendPosition || 'top' 
      },
      title: getTitleOptions(config, 'FSMO Roles Held by Server', 'bar')
    },
    scales: {
      y: { beginAtZero: true, max: 1, ticks: { stepSize: 1 } }
    }
  };

  if (isChartEmpty(chartData)) return null;

  return (
    <div className="chart-widget" style={style}>
      <Bar data={chartData} options={options} />
    </div>
  );
};

/**
 * Chart 3: Active/Inactive Users
 * Doughnut chart
 */
export const ActiveInactiveUsersChart = ({ data, config = {}, style = {} }) => {


  const chartData = useMemo(() => {
    const adData = data?.['Ad Scan'] || data?.Ad || data?.ad || {};
    const allUsers = adData?.['AD Users']?.[0] || adData?.users || [];
    const inactive = adData?.inactive_accounts || [];

    const total = Array.isArray(allUsers) ? allUsers.length : 0;
    const inactiveCount = Array.isArray(inactive) ? inactive.length : 0;
    const active = Math.max(0, total - inactiveCount);

    return {
      labels: ['Active', 'Inactive'],
      datasets: [{
        data: [active, inactiveCount],
        backgroundColor: [DEFAULT_COLORS[0], DEFAULT_COLORS[2]]
      }]
    };
  }, [data]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { 
        display: config.legendPosition !== 'none',
        position: config.legendPosition || 'top' 
      },
      title: getTitleOptions(config, 'AD Users: Active vs Inactive', 'pie')
    }
  };

  if (isChartEmpty(chartData)) return null;

  return (
    <div className="chart-widget" style={style}>
      <Doughnut data={chartData} options={options} />
    </div>
  );
};

/**
 * Chart 4: Password Expiry
 * Doughnut chart showing password expiry policy
 */
export const PasswordExpiryChart = ({ data, config = {}, style = {} }) => {


  const chartData = useMemo(() => {
    const adData = data?.['Ad Scan'] || data?.Ad || data?.ad || {};
    const allUsers = adData?.['AD Users']?.[0] || adData?.users || [];

    let neverExpires = 0;
    if (Array.isArray(allUsers)) {
      neverExpires = allUsers.filter(u =>
        u.PasswordNeverExpires === true || u.password_never_expires === true
      ).length;
    }
    const expires = Math.max(0, allUsers.length - neverExpires);

    return {
      labels: ['Password Expires', 'Never Expires'],
      datasets: [{
        data: [expires, neverExpires],
        backgroundColor: [DEFAULT_COLORS[0], DEFAULT_COLORS[1]]
      }]
    };
  }, [data]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { 
        display: config.legendPosition !== 'none',
        position: config.legendPosition || 'top' 
      },
      title: getTitleOptions(config, 'Password Expiry: Enabled vs Never Expires', 'pie')
    }
  };

  if (isChartEmpty(chartData)) return null;

  return (
    <div className="chart-widget" style={style}>
      <Doughnut data={chartData} options={options} />
    </div>
  );
};

/**
 * Chart 5: Active/Inactive Computers
 * Pie chart
 */
export const ActiveInactiveComputersChart = ({ data, config = {}, style = {} }) => {


  const chartData = useMemo(() => {
    const adData = data?.['Ad Scan'] || data?.Ad || data?.ad || {};
    const allComputers = adData?.Computers || adData?.computers || [];
    const inactiveComputers = adData?.inactive_computers || [];

    const total = Array.isArray(allComputers) ? allComputers.length : 0;
    const inactiveCount = Array.isArray(inactiveComputers) ? inactiveComputers.length : 0;
    const active = Math.max(0, total - inactiveCount);

    return {
      labels: ['Active', 'Inactive'],
      datasets: [{
        data: [active, inactiveCount],
        backgroundColor: [DEFAULT_COLORS[0], DEFAULT_COLORS[2]]
      }]
    };
  }, [data]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { 
        display: config.legendPosition !== 'none',
        position: config.legendPosition || 'top' 
      },
      title: getTitleOptions(config, 'AD Computers: Active vs Inactive', 'pie')
    }
  };

  if (isChartEmpty(chartData)) return null;

  return (
    <div className="chart-widget" style={style}>
      <Pie data={chartData} options={options} />
    </div>
  );
};

/**
 * Chart 6: Group Types
 * Pie chart showing Global, DomainLocal, Universal
 */
export const GroupTypesChart = ({ data, config = {}, style = {} }) => {


  const chartData = useMemo(() => {
    const adData = data?.['Ad Scan'] || data?.Ad || data?.ad || {};
    const groups = adData?.groups || [];

    let global = 0, domainLocal = 0, universal = 0;
    if (Array.isArray(groups)) {
      global = groups.filter(g => g.GroupScope === 'Global' || g.scope === 'Global').length;
      domainLocal = groups.filter(g => g.GroupScope === 'DomainLocal' || g.scope === 'DomainLocal').length;
      universal = groups.filter(g => g.GroupScope === 'Universal' || g.scope === 'Universal').length;
    }

    return {
      labels: ['Global', 'Domain Local', 'Universal'],
      datasets: [{
        data: [global, domainLocal, universal],
        backgroundColor: DEFAULT_COLORS.slice(0, 3)
      }]
    };
  }, [data]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { 
        display: config.legendPosition !== 'none',
        position: config.legendPosition || 'top' 
      },
      title: getTitleOptions(config, 'Group Type Distribution', 'pie')
    }
  };

  if (isChartEmpty(chartData)) return null;

  return (
    <div className="chart-widget" style={style}>
      <Pie data={chartData} options={options} />
    </div>
  );
};

/**
 * Chart 7: OU Membership
 * Bar chart showing objects per OU
 */
export const OUMembershipChart = ({ data, config = {}, style = {} }) => {


  const chartData = useMemo(() => {
    const adData = data?.['Ad Scan'] || data?.Ad || data?.ad || {};
    const ouList = adData?.OU_list || adData?.ou_list || [];

    const topOUs = Array.isArray(ouList) ? ouList.slice(0, 10) : [];
    const labels = topOUs.map(ou => ou.Name || ou.name || 'Unknown');
    const values = topOUs.map(ou => parseInt(ou.member_count || ou.MemberCount || 0));

    return {
      labels,
      datasets: [{
        label: 'Member Count',
        data: values,
        backgroundColor: DEFAULT_COLORS[3]
      }]
    };
  }, [data]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: 'y',
    plugins: {
      legend: { 
        display: !!config.legendPosition && config.legendPosition !== 'none',
        position: config.legendPosition || 'top' 
      },
      title: getTitleOptions(config, 'Objects per Organizational Unit', 'bar')
    }
  };

  if (isChartEmpty(chartData)) return null;

  return (
    <div className="chart-widget" style={style}>
      <Bar data={chartData} options={options} />
    </div>
  );
};

/**
 * Chart 8: GPO Timeline
 * Horizontal bar chart (stacked) showing GPO link settings
 */
export const GPOTimelineChart = ({ data, config = {}, style = {} }) => {


  const chartData = useMemo(() => {
    const adData = data?.['Ad Scan'] || data?.Ad || data?.ad || {};
    const gpos = adData?.gpo_policies || adData?.gpos || [];

    const topGPOs = Array.isArray(gpos) ? gpos.slice(0, 8) : [];
    const labels = topGPOs.map(gpo => gpo.DisplayName || gpo.name || 'Unknown');
    const enabled = topGPOs.map(gpo => gpo.GpoStatus?.includes('Enabled') ? 1 : 0);
    const noOverride = topGPOs.map(gpo => gpo.Enforced === true ? 1 : 0);

    return {
      labels,
      datasets: [
        {
          label: 'Enabled',
          data: enabled,
          backgroundColor: DEFAULT_COLORS[0]
        },
        {
          label: 'No Override',
          data: noOverride,
          backgroundColor: DEFAULT_COLORS[1]
        }
      ]
    };
  }, [data]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: 'y',
    plugins: {
      legend: { 
        display: config.legendPosition !== 'none',
        position: config.legendPosition || 'top' 
      },
      title: getTitleOptions(config, 'GPO Link Settings: Enabled vs NoOverride', 'bar')
    },
    scales: {
      x: { stacked: true },
      y: { stacked: true }
    }
  };

  if (isChartEmpty(chartData)) return null;

  return (
    <div className="chart-widget" style={style}>
      <Bar data={chartData} options={options} />
    </div>
  );
};

// ============================================================================
// LOCALHOST CHARTS (8 charts)
// Data path: data.localhost (matches backend scan structure)
// ============================================================================

/**
 * Chart 9: User Password Status
 * Pie chart
 */
export const UserPasswordStatusChart = ({ data, config = {}, style = {} }) => {


  const chartData = useMemo(() => {
    const localhost = data?.localhost || {};
    const users = localhost?.Users || localhost?.users || [];

    let expiring = 0, noExpiry = 0;
    if (Array.isArray(users)) {
      expiring = users.filter(u => u.PasswordRequired === true).length;
      noExpiry = users.length - expiring;
    }

    return {
      labels: ['Password Expires', 'No Expiry'],
      datasets: [{
        data: [expiring, noExpiry],
        backgroundColor: [DEFAULT_COLORS[0], DEFAULT_COLORS[1]]
      }]
    };
  }, [data]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { 
        display: config.legendPosition !== 'none',
        position: config.legendPosition || 'top' 
      },
      title: getTitleOptions(config, 'Users Password Status Check', 'pie')
    }
  };

  if (isChartEmpty(chartData)) return null;

  return (
    <div className="chart-widget" style={style}>
      <Pie data={chartData} options={options} />
    </div>
  );
};

/**
 * Chart 10: Disk Free vs Used
 * Stacked bar chart
 */
export const DiskFreeVsUsedChart = ({ data, config = {}, style = {} }) => {


  const chartData = useMemo(() => {
    const localhost = data?.localhost || {};
    const disks = localhost?.Disks || localhost?.disks || [];

    const labels = Array.isArray(disks) ? disks.map(d => d.DeviceID || d.drive || 'Unknown') : [];
    const used = Array.isArray(disks) ? disks.map(d => parseFloat(d.Used_GB || d.used || 0)) : [];
    const free = Array.isArray(disks) ? disks.map(d => parseFloat(d.Free_GB || d.free || 0)) : [];

    return {
      labels,
      datasets: [
        {
          label: 'Used (GB)',
          data: used,
          backgroundColor: DEFAULT_COLORS[2]
        },
        {
          label: 'Free (GB)',
          data: free,
          backgroundColor: DEFAULT_COLORS[0]
        }
      ]
    };
  }, [data]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { 
        display: config.legendPosition !== 'none',
        position: config.legendPosition || 'top' 
      },
      title: getTitleOptions(config, 'Disk Free vs Used', 'bar')
    },
    scales: {
      x: { stacked: true },
      y: { stacked: true }
    }
  };

  if (isChartEmpty(chartData)) return null;

  return (
    <div className="chart-widget" style={style}>
      <Bar data={chartData} options={options} />
    </div>
  );
};

/**
 * Chart 11: Firewall Status
 * Bar chart showing Domain, Private, Public profiles
 */
export const FirewallStatusChart = ({ data, config = {}, style = {} }) => {


  const chartData = useMemo(() => {
    const localhost = data?.localhost || {};
    const firewall = localhost?.Firewall || localhost?.firewall || {};

    const domain = firewall?.DomainProfile === 'Enabled' || firewall?.domain === 'Enabled' ? 1 : 0;
    const privateProfile = firewall?.PrivateProfile === 'Enabled' || firewall?.private === 'Enabled' ? 1 : 0;
    const publicProfile = firewall?.PublicProfile === 'Enabled' || firewall?.public === 'Enabled' ? 1 : 0;

    return {
      labels: ['Domain', 'Private', 'Public'],
      datasets: [{
        label: 'Status',
        data: [domain, privateProfile, publicProfile],
        backgroundColor: DEFAULT_COLORS.slice(0, 3)
      }]
    };
  }, [data]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { 
        display: !!config.legendPosition && config.legendPosition !== 'none',
        position: config.legendPosition || 'top' 
      },
      title: getTitleOptions(config, 'Firewall Status per Profile', 'bar')
    },
    scales: {
      y: { beginAtZero: true, max: 1, ticks: { stepSize: 1 } }
    }
  };

  if (isChartEmpty(chartData)) return null;

  return (
    <div className="chart-widget" style={style}>
      <Bar data={chartData} options={options} />
    </div>
  );
};

/**
 * Chart 12: Service Start Modes
 * Pie chart
 */
export const ServiceStartModesChart = ({ data, config = {}, style = {} }) => {


  const chartData = useMemo(() => {
    const localhost = data?.localhost || {};
    const services = localhost?.Services || localhost?.services || [];

    let auto = 0, manual = 0, disabled = 0;
    if (Array.isArray(services)) {
      auto = services.filter(s => s.StartMode === 'Auto').length;
      manual = services.filter(s => s.StartMode === 'Manual').length;
      disabled = services.filter(s => s.StartMode === 'Disabled').length;
    }

    return {
      labels: ['Auto', 'Manual', 'Disabled'],
      datasets: [{
        data: [auto, manual, disabled],
        backgroundColor: DEFAULT_COLORS.slice(0, 3)
      }]
    };
  }, [data]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { 
        display: config.legendPosition !== 'none',
        position: config.legendPosition || 'top' 
      },
      title: getTitleOptions(config, 'Service Start Modes', 'pie')
    }
  };

  if (isChartEmpty(chartData)) return null;

  return (
    <div className="chart-widget" style={style}>
      <Pie data={chartData} options={options} />
    </div>
  );
};

/**
 * Chart 13: Admin Password Status
 * Pie chart
 */
export const AdminPasswordStatusChart = ({ data, config = {}, style = {} }) => {


  const chartData = useMemo(() => {
    const localhost = data?.localhost || {};
    const users = localhost?.Users || localhost?.users || [];

    let admins = [];
    if (Array.isArray(users)) {
      admins = users.filter(u =>
        u.LocalGroup?.includes('Administrators') || u.groups?.includes('Administrators')
      );
    }

    const expiring = admins.filter(u => u.PasswordRequired === true).length;
    const noExpiry = admins.length - expiring;

    return {
      labels: ['Password Expires', 'No Expiry'],
      datasets: [{
        data: [expiring, noExpiry],
        backgroundColor: [DEFAULT_COLORS[0], DEFAULT_COLORS[1]]
      }]
    };
  }, [data]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { 
        display: config.legendPosition !== 'none',
        position: config.legendPosition || 'top' 
      },
      title: getTitleOptions(config, 'Admin Password Status', 'pie')
    }
  };

  if (isChartEmpty(chartData)) return null;

  return (
    <div className="chart-widget" style={style}>
      <Pie data={chartData} options={options} />
    </div>
  );
};

/**
 * Chart 14: Open Ports (Localhost)
 * Pie chart showing sensitive vs other
 */
export const OpenPortsChart = ({ data, config = {}, style = {} }) => {


  const chartData = useMemo(() => {
    const localhost = data?.localhost || {};
    const ports = localhost?.['Open Ports'] || localhost?.open_ports || {};

    const sensitivePorts = [21, 22, 23, 25, 53, 80, 139, 143, 3306, 3389, 8080];
    let sensitive = 0, other = 0;

    Object.keys(ports).forEach(port => {
      if (sensitivePorts.includes(parseInt(port))) {
        sensitive++;
      } else {
        other++;
      }
    });

    return {
      labels: ['Sensitive', 'Other'],
      datasets: [{
        data: [sensitive, other],
        backgroundColor: [DEFAULT_COLORS[2], DEFAULT_COLORS[4]]
      }]
    };
  }, [data]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { 
        display: config.legendPosition !== 'none',
        position: config.legendPosition || 'top' 
      },
      title: getTitleOptions(config, 'Open Ports: Sensitive vs Other', 'pie')
    }
  };

  if (isChartEmpty(chartData)) return null;

  return (
    <div className="chart-widget" style={style}>
      <Pie data={chartData} options={options} />
    </div>
  );
};

/**
 * Chart 15: Password Strength
 * Bar chart
 */
export const PasswordStrengthChart = ({ data, config = {}, style = {} }) => {


  const chartData = useMemo(() => {
    const localhost = data?.localhost || {};
    const passwords = localhost?.['Browser Passwords'] || localhost?.browser_passwords || [];

    let veryStrong = 0, strong = 0, medium = 0, weak = 0;
    if (Array.isArray(passwords)) {
      veryStrong = passwords.filter(p => p.strength === 'Very Strong').length;
      strong = passwords.filter(p => p.strength === 'Strong').length;
      medium = passwords.filter(p => p.strength === 'Medium').length;
      weak = passwords.filter(p => p.strength === 'Weak').length;
    }

    return {
      labels: ['Very Strong', 'Strong', 'Medium', 'Weak'],
      datasets: [{
        label: 'Count',
        data: [veryStrong, strong, medium, weak],
        backgroundColor: [DEFAULT_COLORS[0], DEFAULT_COLORS[5], DEFAULT_COLORS[1], DEFAULT_COLORS[2]]
      }]
    };
  }, [data]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { 
        display: !!config.legendPosition && config.legendPosition !== 'none',
        position: config.legendPosition || 'top' 
      },
      title: getTitleOptions(config, 'Password Strength Distribution', 'bar')
    }
  };

  if (isChartEmpty(chartData)) return null;

  return (
    <div className="chart-widget" style={style}>
      <Bar data={chartData} options={options} />
    </div>
  );
};

/**
 * Chart 16: HIBP Breach Status (Localhost)
 * Pie chart
 */
export const HIBPBreachStatusChart = ({ data, config = {}, style = {} }) => {


  const chartData = useMemo(() => {
    const piiData = data?.PII || {};
    const emails = piiData?.EMAIL_ADDRESS || [];

    let breached = 0, safe = 0;
    if (Array.isArray(emails)) {
      breached = emails.filter(e => e.breached === true).length;
      safe = emails.length - breached;
    }

    return {
      labels: ['Safe', 'Breached'],
      datasets: [{
        data: [safe, breached],
        backgroundColor: [DEFAULT_COLORS[0], DEFAULT_COLORS[2]]
      }]
    };
  }, [data]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { 
        display: config.legendPosition !== 'none',
        position: config.legendPosition || 'top' 
      },
      title: getTitleOptions(config, 'Email Breach Status (HIBP)', 'pie')
    }
  };

  if (isChartEmpty(chartData)) return null;

  return (
    <div className="chart-widget" style={style}>
      <Pie data={chartData} options={options} />
    </div>
  );
};

// ============================================================================
// NETWORK CHARTS (8 charts)
// Data path: data.network_devices (matches backend scan structure)
// ============================================================================

/**
 * Chart 17: Windows Version (Network)
 * Bar chart
 */
export const WindowsVersionChart = ({ data, config = {}, style = {} }) => {


  const chartData = useMemo(() => {
    const network = data?.network_devices || [];
    const versionCounts = {};

    if (Array.isArray(network)) {
      network.forEach(device => {
        const os = device.system?.['Operating System'] || device.os || 'Unknown';
        versionCounts[os] = (versionCounts[os] || 0) + 1;
      });
    }

    return {
      labels: Object.keys(versionCounts),
      datasets: [{
        label: 'Count',
        data: Object.values(versionCounts),
        backgroundColor: DEFAULT_COLORS
      }]
    };
  }, [data]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { 
        display: !!config.legendPosition && config.legendPosition !== 'none',
        position: config.legendPosition || 'top' 
      },
      title: getTitleOptions(config, 'Windows Version Distribution', 'bar')
    }
  };

  if (isChartEmpty(chartData)) return null;

  return (
    <div className="chart-widget" style={style}>
      <Bar data={chartData} options={options} />
    </div>
  );
};

/**
 * Chart 18: Device Type
 * Bar chart
 */
export const DeviceTypeChart = ({ data, config = {}, style = {} }) => {


  const chartData = useMemo(() => {
    const network = data?.network_devices || [];
    const typeCounts = {};

    if (Array.isArray(network)) {
      network.forEach(device => {
        const type = device.device_type || 'Unknown';
        typeCounts[type] = (typeCounts[type] || 0) + 1;
      });
    }

    return {
      labels: Object.keys(typeCounts),
      datasets: [{
        label: 'Count',
        data: Object.values(typeCounts),
        backgroundColor: DEFAULT_COLORS
      }]
    };
  }, [data]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { 
        display: !!config.legendPosition && config.legendPosition !== 'none',
        position: config.legendPosition || 'top' 
      },
      title: getTitleOptions(config, 'Device Type Distribution', 'bar')
    }
  };

  if (isChartEmpty(chartData)) return null;

  return (
    <div className="chart-widget" style={style}>
      <Bar data={chartData} options={options} />
    </div>
  );
};

/**
 * Chart 19: Network Sensitive Ports
 * Doughnut chart
 */
export const NetworkSensitivePortsChart = ({ data, config = {}, style = {} }) => {


  const chartData = useMemo(() => {
    const network = data?.network_devices || [];
    const sensitivePorts = [21, 22, 23, 25, 53, 80, 139, 143, 3306, 3389, 8080];
    let sensitive = 0, other = 0;

    if (Array.isArray(network)) {
      network.forEach(device => {
        const ports = device.open_ports || {};
        Object.keys(ports).forEach(port => {
          if (sensitivePorts.includes(parseInt(port))) {
            sensitive++;
          } else {
            other++;
          }
        });
      });
    }

    return {
      labels: ['Sensitive', 'Other'],
      datasets: [{
        data: [sensitive, other],
        backgroundColor: [DEFAULT_COLORS[2], DEFAULT_COLORS[4]]
      }]
    };
  }, [data]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { 
        display: config.legendPosition !== 'none',
        position: config.legendPosition || 'top' 
      },
      title: getTitleOptions(config, 'Sensitive vs Other Open Ports', 'pie')
    }
  };

  if (isChartEmpty(chartData)) return null;

  return (
    <div className="chart-widget" style={style}>
      <Doughnut data={chartData} options={options} />
    </div>
  );
};

/**
 * Chart 20: Common Software Status (Network)
 * Pie chart
 */
export const CommonSoftwareStatusChart = ({ data, config = {}, style = {} }) => {


  const chartData = useMemo(() => {
    const network = data?.network_devices || [];
    let upToDate = 0, outdated = 0;

    if (Array.isArray(network)) {
      network.forEach(device => {
        const software = device.system?.['Installed Applications'] || [];
        if (Array.isArray(software)) {
          software.forEach(app => {
            if (app.status === 'Up-to-date') upToDate++;
            else outdated++;
          });
        }
      });
    }

    return {
      labels: ['Up-to-date', 'Outdated'],
      datasets: [{
        data: [upToDate, outdated],
        backgroundColor: [DEFAULT_COLORS[0], DEFAULT_COLORS[1]]
      }]
    };
  }, [data]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { 
        display: config.legendPosition !== 'none',
        position: config.legendPosition || 'top' 
      },
      title: getTitleOptions(config, 'Common Software Status', 'pie')
    }
  };

  if (isChartEmpty(chartData)) return null;

  return (
    <div className="chart-widget" style={style}>
      <Pie data={chartData} options={options} />
    </div>
  );
};

/**
 * Chart 21: Browser Status (Network)
 * Bubble chart showing password analysis
 */
export const BrowserStatusChart = ({ data, config = {}, style = {} }) => {


  const chartData = useMemo(() => {
    const network = data?.network_devices || [];
    const passwords = [];

    if (Array.isArray(network)) {
      network.forEach((device, idx) => {
        const browserPws = device.system?.['Browser Passwords'] || [];
        if (Array.isArray(browserPws)) {
          browserPws.forEach((pw, pwIdx) => {
            passwords.push({
              x: idx,
              y: pw.strength === 'Strong' ? 2 : pw.strength === 'Medium' ? 1 : 0,
              r: 5
            });
          });
        }
      });
    }

    return {
      datasets: [{
        label: 'Password Strength',
        data: passwords.slice(0, 20),
        backgroundColor: DEFAULT_COLORS[5]
      }]
    };
  }, [data]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { 
        display: !!config.legendPosition && config.legendPosition !== 'none',
        position: config.legendPosition || 'top' 
      },
      title: getTitleOptions(config, 'Browsers Password Analysis', 'bar')
    }
  };

  if (isChartEmpty(chartData)) return null;

  return (
    <div className="chart-widget" style={style}>
      <Bubble data={chartData} options={options} />
    </div>
  );
};

/**
 * Chart 22: HIBP Status (Network)
 * Pie chart
 */
export const HIBPStatusChart = ({ data, config = {}, style = {} }) => {


  const chartData = useMemo(() => {
    const piiData = data?.PII || {};
    const emails = piiData?.EMAIL_ADDRESS || [];

    let breached = 0, safe = 0;
    if (Array.isArray(emails)) {
      breached = emails.filter(e => e.breached === true).length;
      safe = emails.length - breached;
    }

    return {
      labels: ['Safe', 'Breached'],
      datasets: [{
        data: [safe, breached],
        backgroundColor: [DEFAULT_COLORS[0], DEFAULT_COLORS[2]]
      }]
    };
  }, [data]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { 
        display: config.legendPosition !== 'none',
        position: config.legendPosition || 'top' 
      },
      title: getTitleOptions(config, 'HIBP Scan', 'pie')
    }
  };

  if (isChartEmpty(chartData)) return null;

  return (
    <div className="chart-widget" style={style}>
      <Pie data={chartData} options={options} />
    </div>
  );
};

/**
 * Chart 23: System Age Distribution
 * Pie chart
 */
export const SystemAgeDistributionChart = ({ data, config = {}, style = {} }) => {


  const chartData = useMemo(() => {
    const network = data?.network_devices || [];
    const ageGroups = { '0-1yr': 0, '1-3yr': 0, '3-5yr': 0, '5+yr': 0 };

    if (Array.isArray(network)) {
      network.forEach(device => {
        const age = parseFloat(device.system?.['System Age'] || device.age || 0);
        if (age <= 1) ageGroups['0-1yr']++;
        else if (age <= 3) ageGroups['1-3yr']++;
        else if (age <= 5) ageGroups['3-5yr']++;
        else ageGroups['5+yr']++;
      });
    }

    return {
      labels: Object.keys(ageGroups),
      datasets: [{
        data: Object.values(ageGroups),
        backgroundColor: DEFAULT_COLORS.slice(0, 4)
      }]
    };
  }, [data]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { 
        display: config.legendPosition !== 'none',
        position: config.legendPosition || 'top' 
      },
      title: getTitleOptions(config, 'Device Age Distribution', 'pie')
    }
  };

  if (isChartEmpty(chartData)) return null;

  return (
    <div className="chart-widget" style={style}>
      <Pie data={chartData} options={options} />
    </div>
  );
};

/**
 * Chart 24: RDP Status
 * Doughnut chart
 */
export const RDPStatusChart = ({ data, config = {}, style = {} }) => {
  const chartData = useMemo(() => {
    const network = data?.network_devices || [];
    let enabled = 0, disabled = 0;

    if (Array.isArray(network)) {
      network.forEach(device => {
        const rdp = device.system?.['RDP Status'] || device.rdp || 'Disabled';
        if (rdp === 'Enabled') enabled++;
        else disabled++;
      });
    }

    return {
      labels: ['Enabled', 'Disabled'],
      datasets: [{
        data: [enabled, disabled],
        backgroundColor: [DEFAULT_COLORS[2], DEFAULT_COLORS[0]]
      }]
    };
  }, [data]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { 
        display: config.legendPosition !== 'none',
        position: config.legendPosition || 'bottom' 
      },
      title: getTitleOptions(config, 'RDP Status', 'pie')
    }
  };

  if (isChartEmpty(chartData)) return null;

  return (
    <div className="chart-widget" style={style}>
      <Doughnut data={chartData} options={options} />
    </div>
  );
};

/**
 * Chart 25: Network Firewall Status
 * Pie chart showing Fully Enabled, Partially Enabled, Fully Disabled
 * Based on network_devices data and analyzeNetworkFirewall logic
 */
export const NetworkFirewallStatusChart = ({ data, config = {}, style = {}, theme = {} }) => {
  const chartData = useMemo(() => {
    const scanDataSource = data?.scanData || data?.data || data;
    const network = scanDataSource?.network_devices || [];
    const validDevices = network.filter(device =>
      device?.system && device.system !== "WMI Error"
    );

    let fullyEnabled = 0, partiallyEnabled = 0, fullyDisabled = 0;

    validDevices.forEach(device => {
      const firewall = device?.system?.Firewall;
      if (!firewall || typeof firewall !== 'object') {
        fullyDisabled++;
        return;
      }

      const domain = firewall.Domain === true;
      const privateNet = firewall.Private === true;
      const publicNet = firewall.Public === true;
      const enabledCount = [domain, privateNet, publicNet].filter(Boolean).length;

      if (enabledCount === 3) {
        fullyEnabled++;
      } else if (enabledCount > 0) {
        partiallyEnabled++;
      } else {
        fullyDisabled++;
      }
    });

    return {
      labels: ['Fully Enabled', 'Partially Enabled', 'Fully Disabled'],
      datasets: [{
        data: [fullyEnabled, partiallyEnabled, fullyDisabled],
        backgroundColor: [
          theme?.statusColors?.green || '#6fca42',
          theme?.statusColors?.yellow || '#fdbc13',
          theme?.statusColors?.red || '#d71920'
        ]
      }]
    };
  }, [data, theme]);

  const chartType = config.chartType || 'donut';

  const titleStyle = getLabelStyle(config, 'title', {
    fontSize: '16px',
    fontWeight: '600',
    color: '#212529',
    textAlign: 'center',
    marginBottom: '15px'
  }, 'network-firewall-status-chart', data);

  const titleText = getLabel(config, 'network-firewall-status-chart', 'title') || 'Network Firewall Status';

  const options = {
    indexAxis: chartType === 'bar' ? 'y' : 'x',
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { 
        display: config.legendPosition !== 'none',
        position: config.legendPosition || 'top',
        labels: {
          generateLabels: (chart) => {
            const data = chart.data;
            if (data.labels.length && data.datasets.length) {
              const dataset = data.datasets[0];
              return data.labels.map((label, i) => {
                const value = dataset.data[i] || 0;
                return {
                  text: `${label}: ${value}`,
                  fillStyle: dataset.backgroundColor[i],
                  hidden: false,
                  index: i
                };
              });
            }
            return [];
          }
        }
      },
      title: { display: false }, // Disable Chart.js native title to use React title instead
      tooltip: {
        callbacks: {
          label: (context) => {
            const label = context.label || '';
            const value = context.parsed.y !== undefined ? context.parsed.y : (context.parsed.x !== undefined ? context.parsed.x : context.parsed);
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = total > 0 ? Math.round((value / total) * 100) : 0;
            return `${label}: ${value} (${percentage}%)`;
          }
        }
      }
    },
    scales: (['bar', 'line'].includes(chartType)) ? {
      [chartType === 'bar' ? 'x' : 'y']: {
        beginAtZero: true,
        ticks: { precision: 0 }
      }
    } : undefined
  };

  if (isChartEmpty(chartData)) return null;

  const renderChart = () => {
    switch (chartType) {
      case 'pie': return <Pie data={chartData} options={options} />;
      case 'bar': return <Bar data={chartData} options={options} />;
      case 'line': {
        // Line chart needs a single color or point colors
        const lineData = {
          ...chartData,
          datasets: [{
            ...chartData.datasets[0],
            borderColor: '#3b82f6',
            backgroundColor: 'rgba(59, 130, 246, 0.1)',
            fill: true,
            tension: 0.4,
            pointBackgroundColor: chartData.datasets[0].backgroundColor,
            pointBorderColor: '#fff',
            pointRadius: 6,
            pointHoverRadius: 8
          }]
        };
        return <Line data={lineData} options={options} />;
      }
      case 'donut':
      default:
        return <Doughnut data={chartData} options={options} />;
    }
  };

  return (
    <div className="chart-widget" style={style}>
      <h3 className="chart-title" style={titleStyle}>{titleText}</h3>
      <div className="chart-container">
        {renderChart()}
      </div>
    </div>
  );
};

// Export all chart widgets
export default {
  // AD Charts (8)
  ADObjectTypesChart,
  ADObjectsPieChart,
  FSMORolesChart,
  ActiveInactiveUsersChart,
  PasswordExpiryChart,
  ActiveInactiveComputersChart,
  GroupTypesChart,
  OUMembershipChart,
  GPOTimelineChart,

  // Localhost Charts (8)
  UserPasswordStatusChart,
  DiskFreeVsUsedChart,
  FirewallStatusChart,
  ServiceStartModesChart,
  AdminPasswordStatusChart,
  OpenPortsChart,
  PasswordStrengthChart,
  HIBPBreachStatusChart,

  // Network Charts (9)
  WindowsVersionChart,
  DeviceTypeChart,
  NetworkSensitivePortsChart,
  CommonSoftwareStatusChart,
  BrowserStatusChart,
  HIBPStatusChart,
  SystemAgeDistributionChart,
  RDPStatusChart,
  NetworkFirewallStatusChart
};
